import { useRef, useState, useMemo } from 'react';
import { Formik, FormikProps } from 'formik';
import { useRouter } from '@uirouter/react';

import { notifySuccess } from '@/portainer/services/notifications';
import { useCanExit } from '@/react/hooks/useCanExit';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useNamespaceAuthorizationChecker } from '@/react/hooks/useUser';
import { useNamespacesQuery } from '@/react/kubernetes/namespaces/queries/useNamespacesQuery';
import { useCreateStack } from '@/react/common/stacks/queries/useCreateStack/useCreateStack';
import { createWebhookId } from '@/portainer/helpers/webhookHelper';

import { confirm, confirmGenericDiscard } from '@@/modals/confirm';

import { useUpdateHelmReleaseMutation } from '../helmReleaseQueries/useUpdateHelmReleaseMutation';

import { HelmInstallFormValues } from './types';
import { helmInstallFormValidationSchema } from './validation';
import { HelmInstallInnerForm } from './HelmInstallInnerForm';

export function HelmInstallForm() {
  const environmentId = useEnvironmentId();
  const router = useRouter();

  const [previewIsValid, setPreviewIsValid] = useState(false);

  const namespacesQuery = useNamespacesQuery(environmentId);
  // Only offer namespaces where the user can create applications (a Helm
  // install deploys application workloads), and never system namespaces, so
  // the form can't target a namespace the install would be rejected for.
  const { canAccess, isLoading: accessLoading } =
    useNamespaceAuthorizationChecker('K8sApplicationsW');
  // canAccess returns false until the access query resolves. Wait for both
  // queries before filtering so the list (and the default namespace) resolve in
  // a single step, the same way the form waits on the namespaces query itself —
  // otherwise the list can flash empty and reinitialize the form a second time.
  const namespaces =
    namespacesQuery.isLoading || accessLoading
      ? []
      : Object.values(namespacesQuery.data ?? {})
          .filter((ns) => !ns.IsSystem && canAccess(ns.Name))
          .map((ns) => ({
            label: ns.Name,
            value: ns.Name,
          }));

  const defaultNamespace =
    namespaces.find((ns) => ns.value === 'default')?.value ||
    namespaces[0]?.value ||
    '';

  const formikRef = useRef<FormikProps<HelmInstallFormValues>>(null);
  const [webhookId] = useState(() => createWebhookId());

  const initialValues: HelmInstallFormValues = useMemo(
    () => ({
      useGitOps: true,
      // Git configuration
      SourceId: undefined,
      RepositoryURL: '',
      RepositoryURLValid: false,
      // Helm-specific Git fields
      HelmChartPath: '',
      HelmValuesFiles: [],
      RepositoryReferenceName: '',
      // Auto update configuration
      AutoUpdate: {
        RepositoryAutomaticUpdates: false,
        ForcePullImage: false,
        RepositoryAutomaticUpdatesForce: false,
      },
      webhookId,
      // Atomic option (rollback on failure)
      atomic: false,
      // Deploy configuration
      namespace: defaultNamespace,
      name: '',
      version: '',
      values: '',
      repo: '',
      registryId: undefined,
      chartName: undefined,
      chartRepo: undefined,
    }),
    [defaultNamespace, webhookId]
  );

  useCanExit(() => !formikRef.current?.dirty || confirmGenericDiscard());

  const installHelmChartMutation = useUpdateHelmReleaseMutation(environmentId);
  const createStackMutation = useCreateStack();

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={helmInstallFormValidationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnChange
      validateOnBlur
      validateOnMount
    >
      {() => (
        <HelmInstallInnerForm
          namespaces={namespaces}
          setPreviewIsValid={setPreviewIsValid}
        />
      )}
    </Formik>
  );

  async function handleSubmit(values: HelmInstallFormValues) {
    if (values.useGitOps) {
      await gitHelmInstall(values);
    } else {
      await manualHelmInstall(values);
    }
  }

  async function gitHelmInstall(values: HelmInstallFormValues) {
    if (!previewIsValid) {
      const confirmed = await confirm({
        title: 'Chart validation failed',
        message:
          'The Helm manifest preview validation failed, which may indicate configuration issues. This can be normal when creating new resources. Do you want to proceed with the installation?',
      });
      if (!confirmed) {
        return;
      }
    }

    await createStackMutation.mutateAsync(
      {
        type: 'kubernetes',
        method: 'git',
        payload: {
          name: values.name.trim(),
          environmentId,
          namespace: values.namespace,
          composeFormat: false,
          // This is a workaround to use the root directory of the repo as the chart path.
          // The backend uses the value of the Helm Chart Path to determine if the Kubernetes Stack deployment is a Helm Chart or a Manifest deployment.
          // If it has a value then it's assumed to be a Helm Chart deployment, otherwise it's assumed to be a Manifest deployment. Since changing this
          // in the backend would be a breaking change I need to pass a non-empty path here.
          helmChartPath: values.HelmChartPath || './',
          helmValuesFiles: values.HelmValuesFiles || [],
          webhook: values.webhookId,
          git: {
            SourceId: values.SourceId,
            RepositoryReferenceName: values.RepositoryReferenceName,
            // URL and auth are resolved from the selected Source on the backend.
            RepositoryURL: '',
            AutoUpdate: values.AutoUpdate,
          },
        },
      },
      {
        onSuccess() {
          notifySuccess(
            'Success',
            'Helm chart from Git repository successfully deployed'
          );
          formikRef.current?.resetForm();
          router.stateService.go('kubernetes.applications');
        },
      }
    );
  }

  async function manualHelmInstall(values: HelmInstallFormValues) {
    if (!previewIsValid) {
      const confirmed = await confirm({
        title: 'Chart validation failed',
        message:
          'The Helm manifest preview validation failed, which may indicate configuration issues. This can be normal when creating new resources. Do you want to proceed with the installation?',
      });
      if (!confirmed) {
        return;
      }
    }

    if (!values.chartName) {
      return;
    }

    await installHelmChartMutation.mutateAsync(
      {
        name: values.name.trim(),
        namespace: values.namespace,
        chart: values.chartName,
        version: values.version,
        values: values.values,
        repo: values.registryId ? undefined : values.chartRepo,
        registryId: values.registryId,
      },
      {
        onSuccess() {
          notifySuccess('Success', 'Helm chart successfully installed');

          formikRef.current?.resetForm();
          router.stateService.go('kubernetes.applications');
        },
      }
    );
  }
}
